// Country list used by phone-number dropdowns in OrderForm/TeleSales.
// India is first because that's where the bulk of orders come from; the
// Gulf and common diaspora destinations follow before the rest of the
// world. The dropdown value is the country `name` (kept canonical so
// the existing `country: …` payload field stays backward-compatible),
// and helpers below expose the dial code and flag for display.

// `nsn` is the set of valid national-significant-number (local subscriber)
// lengths, i.e. the digit count AFTER the country/dial code. India is a
// strict 10; the Gulf markets we serve are mostly 8–9. These power the
// phone-field validation and the paste-and-peel cap in the forms, so a
// Saudi (+966 → 9-digit) number is accepted just like an Indian one.
const COUNTRIES = [
  { code: '+91', name: 'India', flag: '🇮🇳', nsn: [10] },
  { code: '+971', name: 'United Arab Emirates', flag: '🇦🇪', nsn: [9] },
  { code: '+966', name: 'Saudi Arabia', flag: '🇸🇦', nsn: [9] },
  { code: '+974', name: 'Qatar', flag: '🇶🇦', nsn: [8] },
  { code: '+965', name: 'Kuwait', flag: '🇰🇼', nsn: [8] },
  { code: '+968', name: 'Oman', flag: '🇴🇲', nsn: [8] },
  { code: '+973', name: 'Bahrain', flag: '🇧🇭', nsn: [8] },
  { code: '+1', name: 'United States', flag: '🇺🇸', nsn: [10] },
  { code: '+1', name: 'Canada', flag: '🇨🇦', nsn: [10] },
  { code: '+44', name: 'United Kingdom', flag: '🇬🇧', nsn: [10] },
  { code: '+61', name: 'Australia', flag: '🇦🇺', nsn: [9] },
  { code: '+64', name: 'New Zealand', flag: '🇳🇿', nsn: [8, 9, 10] },
  { code: '+65', name: 'Singapore', flag: '🇸🇬', nsn: [8] },
  { code: '+60', name: 'Malaysia', flag: '🇲🇾', nsn: [9, 10] },
  { code: '+94', name: 'Sri Lanka', flag: '🇱🇰', nsn: [9] },
  { code: '+960', name: 'Maldives', flag: '🇲🇻', nsn: [7] },
  { code: '+977', name: 'Nepal', flag: '🇳🇵', nsn: [10] },
  { code: '+880', name: 'Bangladesh', flag: '🇧🇩', nsn: [10] },
  { code: '+92', name: 'Pakistan', flag: '🇵🇰', nsn: [10] },
  { code: '+49', name: 'Germany', flag: '🇩🇪', nsn: [10, 11] },
  { code: '+33', name: 'France', flag: '🇫🇷', nsn: [9] },
  { code: '+39', name: 'Italy', flag: '🇮🇹', nsn: [9, 10] },
  { code: '+34', name: 'Spain', flag: '🇪🇸', nsn: [9] },
  { code: '+31', name: 'Netherlands', flag: '🇳🇱', nsn: [9] },
  { code: '+41', name: 'Switzerland', flag: '🇨🇭', nsn: [9] },
  { code: '+46', name: 'Sweden', flag: '🇸🇪', nsn: [7, 8, 9] },
  { code: '+47', name: 'Norway', flag: '🇳🇴', nsn: [8] },
  { code: '+353', name: 'Ireland', flag: '🇮🇪', nsn: [9] },
  { code: '+81', name: 'Japan', flag: '🇯🇵', nsn: [9, 10] },
  { code: '+82', name: 'South Korea', flag: '🇰🇷', nsn: [9, 10] },
  { code: '+86', name: 'China', flag: '🇨🇳', nsn: [11] },
  { code: '+852', name: 'Hong Kong', flag: '🇭🇰', nsn: [8] },
  { code: '+66', name: 'Thailand', flag: '🇹🇭', nsn: [9] },
  { code: '+62', name: 'Indonesia', flag: '🇮🇩', nsn: [9, 10, 11, 12] },
  { code: '+63', name: 'Philippines', flag: '🇵🇭', nsn: [10] },
  { code: '+27', name: 'South Africa', flag: '🇿🇦', nsn: [9] },
  { code: '+20', name: 'Egypt', flag: '🇪🇬', nsn: [10] },
  { code: '+55', name: 'Brazil', flag: '🇧🇷', nsn: [10, 11] },
  { code: '+52', name: 'Mexico', flag: '🇲🇽', nsn: [10] }
]

export const DEFAULT_COUNTRY_NAME = 'India'

// Fallback valid lengths for any country without an explicit `nsn`
// (and the catch-all when a name doesn't resolve). Deliberately wide so
// we never block a legitimate international number we haven't tabulated.
const DEFAULT_NSN_LENGTHS = [7, 8, 9, 10, 11, 12]

export function getCountries () {
  return COUNTRIES
}

export function findCountryByName (name) {
  return COUNTRIES.find((country) => country.name === name) || null
}

// Build options for AppDropdown. `value` is the country name (kept for
// payload backward-compatibility); `shortLabel` is what shows in the
// closed trigger ("🇮🇳 +91"); `label` is the searchable list row
// ("🇮🇳 +91 · India").
export function countryDropdownOptions () {
  return COUNTRIES.map((country) => ({
    value: country.name,
    shortLabel: `${country.flag} ${country.code}`,
    label: `${country.flag}  ${country.code}  ·  ${country.name}`
  }))
}

// Dial code for the selected country name, without the leading +.
// Used to build wa.me links and `+XX` display prefixes.
export function dialCodeFor (name) {
  const country = findCountryByName(name)
  if (!country) return '91'
  return country.code.replace(/^\+/, '')
}

// Find the country whose dial code prefixes the given digit string.
// Used to auto-switch the country dropdown when an agent pastes a
// number like "+919207948134" or "+966500000000".
//
// Longest-prefix match: when a number could match multiple codes
// (e.g. a hypothetical +1 and +1809), the longer code wins. For
// codes shared across countries (US and Canada both +1) the first
// entry in COUNTRIES is returned, matching dropdown order.
export function findCountryByDialCode (digits) {
  const cleaned = String(digits || '').replace(/\D/g, '')
  if (!cleaned) return null
  const sorted = COUNTRIES.slice().sort((a, b) => {
    const aLen = a.code.replace(/^\+/, '').length
    const bLen = b.code.replace(/^\+/, '').length
    return bLen - aLen
  })
  return sorted.find((c) => cleaned.startsWith(c.code.replace(/^\+/, ''))) || null
}

// Valid local-subscriber-number lengths for the selected country name.
// Defaults to a wide range for unlisted countries so we never block a
// legitimate number we haven't tabulated.
export function phoneLengthsFor (name) {
  const country = findCountryByName(name)
  return (country && country.nsn) || DEFAULT_NSN_LENGTHS
}

// Longest valid local-number length — used to cap the stored phone and
// to decide when a pasted number carries a country-code prefix.
export function maxPhoneLengthFor (name) {
  return Math.max(...phoneLengthsFor(name))
}

// True when `digits` (any format; non-digits ignored) has a valid local
// length for the given country.
export function isValidPhoneLength (name, digits) {
  const len = String(digits || '').replace(/\D/g, '').length
  return phoneLengthsFor(name).includes(len)
}
